<template lang="pug">
.c-customize-page
  //- Header Section
  section.c-customize-page__header
    .c-customize-page__header-content
      h1.c-customize-page__heading Customize
      p.c-customize-page__subtitle Manage static pages and custom content

    .c-customize-page__header-actions
      el-button.c-customize-action-btn(@click="$router.push(`/manage-content/media/static-pages/add`)")
        fai(type="fal" icon="plus-circle").me-2
        span Add New

  //- Content Section
  .c-customize-content
    //- Table Section
    section.c-customize-table-section
      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--title
            .c-skeleton-cell.c-skeleton-cell--slug

      //- Table
      template(v-else-if="!apiError")
        el-table(
          v-if="items.items.length > 0"
          :data="items.items"
          @row-click="$router.push(`static-pages/${$event.id}`)"
          style="width: 100%"
        ).clickable-rows

          el-table-column(prop="title" label="Title" min-width="200")
            template(#default="scope")
              .lh-sm
                .font-black {{ scope.row.title }}

          el-table-column(prop="slug" label="Slug" min-width="200")
            template(#default="scope")
              .lh-sm
                .font-medium {{ scope.row.slug }}

        //- Empty State
        .c-customize-empty(v-else)
          .c-customize-empty__icon
            fai(type="fal" icon="file-alt").size-48
          h3.c-customize-empty__title No static pages found
          p.c-customize-empty__text Create your first static page by clicking the "Add New" button above.

  //- Error State
  .c-customize-error(v-if="apiError && !loading")
    .c-customize-error__icon
      fai(type="fal" icon="exclamation-triangle").size-48
    h3.c-customize-error__title Error loading static pages
    p.c-customize-error__text {{ apiError }}
    el-button.c-customize-error__button(
      @click="getItems"
      type="primary"
    ) Retry
</template>

<script lang="ts">
import {
  reactive, onMounted, defineComponent, ref,
} from 'vue';
import shared from '../shared';
import { findAllStaticPages } from '@/graphs/gql';

export default defineComponent({
  name: 'ManageContent_Media_StaticPages',
  setup() {
    const { makeGraphRequest, getAuthToken, loading } = shared();

    // Initialize loading as true to show skeleton immediately
    loading.value = true;

    const items = reactive({ items: [] });
    const apiError = ref(null);

    async function getItems() {
      try {
        loading.value = true;
        apiError.value = null;

        const response = await makeGraphRequest('query', findAllStaticPages, {}, getAuthToken());
        items.items = response.data.findAllStaticPages;
      } catch (err) {
        console.error('Error fetching static pages:', err);
        apiError.value = err.message || 'Failed to fetch static pages';
      } finally {
        loading.value = false;
      }
    }

    onMounted(async () => {
      await getItems();
    });

    return {
      items,
      loading,
      apiError,
      getItems,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-customize-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__header-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
}

// Action button styles
.c-customize-action-btn {
  height: 36px;
  font-size: 14px;
  border-radius: 8px;
  background-color: white !important;
  border: 1.5px solid $color-purple-text !important;
  color: $color-purple-text !important;

  &:hover,
  &:focus {
    background-color: rgba($color-purple-text, 0.08) !important;
    border-color: color.adjust($color-purple-text, $lightness: -8%) !important;
    color: color.adjust($color-purple-text, $lightness: -8%) !important;
  }

  :deep(span) {
    font-weight: 500;
  }

  :deep(.me-2) {
    margin-right: 0.5rem;
  }
}

.c-customize-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

// Table Section
.c-customize-table-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  overflow: hidden;
  width: 100%;
  max-width: 100%;

  :deep(.el-table__body-wrapper) {
    overflow-x: hidden !important;
  }

  :deep(.el-table__header-wrapper) {
    overflow-x: hidden !important;
  }

  :deep(.el-table) {
    width: 100% !important;
    border-radius: 0;
    table-layout: fixed;

    .cell {
      color: $gray-700;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      white-space: nowrap;
      padding: 0.5rem 0.5rem;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    th > .cell {
      text-transform: capitalize;
      color: $gray-700;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: $gray-50;
      border-bottom: 1px solid $gray-100;
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid $gray-100;
      padding: 0.625rem 0;
    }

    .el-table__row {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: $gray-50;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }
  }
}

// Font utilities
.font-black {
  font-weight: 600;
  color: $gray-900;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.lh-sm {
  line-height: 1.25;
}

// Skeleton Loader
.c-skeleton-loader {
  width: 100%;
  padding: 1rem 0;
}

.c-skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  gap: 1rem;
  border-bottom: 1px solid $gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.c-skeleton-cell {
  height: 20px;
  background: linear-gradient(90deg, #F0F0F0 25%, #E0E0E0 50%, #F0F0F0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;

  &--title {
    width: 220px;
    flex-shrink: 0;
  }

  &--slug {
    flex: 1;
    min-width: 220px;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Empty State
.c-customize-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;

  &__icon {
    color: $gray-400;
    margin-bottom: 1rem;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0 0 0.5rem 0;
  }

  &__text {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
  }
}

// Error State
.c-customize-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;

  &__icon {
    color: #f56c6c;
    margin-bottom: 1rem;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0 0 0.5rem 0;
  }

  &__text {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0 0 1.5rem 0;
  }

  &__button {
    background-color: #f56c6c !important;
    border-color: #f56c6c !important;
    color: white !important;

    &:hover {
      background-color: #e45656 !important;
      border-color: #e45656 !important;
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .c-customize-page {
    padding: 1.5rem;

    &__heading {
      font-size: 1.5rem;
    }

    &__header {
      gap: 1rem;
    }
  }
}
</style>
